import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Upload, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { api, formatApiError, API } from "@/lib/api";
import { DEFAULT_CONTENT } from "@/lib/content";

const inputCls =
  "w-full border border-champagne bg-white/70 px-3 py-2 text-sm text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:border-burgundy transition-colors";
const labelCls = "block text-[10px] font-semibold uppercase tracking-[0.25em] text-rose mb-1";
const sectionCls = "border border-champagne bg-ivory p-6 md:p-8 scroll-mt-8";
const headingCls = "font-serif text-2xl font-medium text-burgundy";

const BLANKS = {
  services: { title: "", context: "", detail: "" },
  credits: { type: "", production: "", role: "Intimacy Coordinator", byline: "" },
  training: { title: "", org: "", detail: "" },
  testimonials: { quote: "", name: "", role: "", featured: false },
};

const CHAPTER_LABELS = [
  ["about", "About section"],
  ["resume", "Résumé section"],
  ["training", "Training section"],
  ["services", "Services section"],
  ["testimonials", "Testimonials section"],
  ["contact", "Contact section"],
];

const FORM_FIELDS = [
  ["name", "Name field"],
  ["email", "Email field"],
  ["position", "Job position field"],
  ["production", "Production field"],
  ["message", "Message field"],
];

// Fields whose "required?" toggle is exposed in the admin panel
const REQUIRABLE_FIELDS = [
  ["name", "Name"],
  ["email", "Email"],
  ["position", "Job position"],
  ["production", "Production / Company"],
  ["projectType", "Project type"],
  ["union", "Union / Non-Union"],
  ["message", "Message"],
];

export const ContentEditor = () => {
  const [content, setContent] = useState(null);
  const [raw, setRaw] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const dragRef = useRef(null);
  const saveRef = useRef(null);

  useEffect(() => {
    api
      .get("/content")
      .then(({ data }) => {
        const merged = { ...DEFAULT_CONTENT, ...data };
        merged.bio = {
          ...merged.bio,
          tags: (merged.bio.tags || []).map((t) =>
            typeof t === "string" ? { text: t, url: "", underline: false } : { url: "", underline: false, ...t }
          ),
        };
        setContent(merged);
        setRaw({
          marquee: merged.marquee.words.join(", "),
          contactLines: merged.contact.lines.join("\n"),
          projectTypes: (merged.contact.projectTypes || []).join("\n"),
        });
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    const handler = () => saveRef.current?.();
    window.addEventListener("cms-save", handler);
    return () => window.removeEventListener("cms-save", handler);
  }, []);

  if (!content || !raw) {
    return (
      <main className="mx-auto max-w-5xl px-6 md:px-12 py-12" data-testid="content-editor-loading">
        <p className="text-sm uppercase tracking-[0.3em] text-charcoal/50">Loading content…</p>
      </main>
    );
  }

  const setBio = (key, value) => setContent((c) => ({ ...c, bio: { ...c.bio, [key]: value } }));
  const setHero = (key, value) => setContent((c) => ({ ...c, hero: { ...c.hero, [key]: value } }));
  const setChapter = (id, key, value) =>
    setContent((c) => ({ ...c, chapters: { ...c.chapters, [id]: { ...c.chapters[id], [key]: value } } }));
  const setResumeCard = (key, value) => setContent((c) => ({ ...c, resumeCard: { ...c.resumeCard, [key]: value } }));
  const setContactField = (key, value) => setContent((c) => ({ ...c, contact: { ...c.contact, [key]: value } }));
  const setContactNested = (group, key, value) =>
    setContent((c) => ({ ...c, contact: { ...c.contact, [group]: { ...c.contact[group], [key]: value } } }));
  const setFooter = (key, value) => setContent((c) => ({ ...c, footer: { ...c.footer, [key]: value } }));
  const setHeader = (key, value) => setContent((c) => ({ ...c, header: { ...c.header, [key]: value } }));
  const setHeaderTab = (id, value) =>
    setContent((c) => ({ ...c, header: { ...c.header, tabs: { ...c.header.tabs, [id]: value } } }));
  const setColor = (key, value) => setContent((c) => ({ ...c, colors: { ...c.colors, [key]: value } }));
  const setItem = (list, i, field, value) =>
    setContent((c) => ({ ...c, [list]: c[list].map((item, idx) => (idx === i ? { ...item, [field]: value } : item)) }));
  const addItem = (list) => setContent((c) => ({ ...c, [list]: [...c[list], { ...BLANKS[list] }] }));
  const removeItem = (list, i) => setContent((c) => ({ ...c, [list]: c[list].filter((_, idx) => idx !== i) }));

  const getArr = (c, path) => path.split(".").reduce((o, k) => o[k], c) || [];
  const setArr = (c, path, arr) =>
    path.includes(".")
      ? { ...c, resources: { ...c.resources, [path.split(".")[1]]: arr } }
      : { ...c, [path]: arr };
  const moveItem = (path, i, dir) =>
    setContent((c) => {
      const arr = [...getArr(c, path)];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return c;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return setArr(c, path, arr);
    });
  const startDrag = (path, i) => (e) => {
    dragRef.current = { path, i };
    e.dataTransfer.effectAllowed = "move";
  };
  const dropOn = (path, i) => (e) => {
    e.preventDefault();
    const d = dragRef.current;
    if (d && d.path === path && d.i !== i) {
      setContent((c) => {
        const arr = [...getArr(c, path)];
        const [moved] = arr.splice(d.i, 1);
        arr.splice(i, 0, moved);
        return setArr(c, path, arr);
      });
    }
    dragRef.current = null;
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...content,
        marquee: { words: raw.marquee.split(",").map((w) => w.trim()).filter(Boolean) },
        contact: {
          ...content.contact,
          lines: raw.contactLines.split("\n").map((l) => l.trim()).filter(Boolean),
          projectTypes: raw.projectTypes.split("\n").map((l) => l.trim()).filter(Boolean),
        },
      };
      await api.put("/admin/content", payload);
      setContent(payload);
      toast.success("Site content saved — it's live on your site now.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };
  saveRef.current = save;

  const uploadPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      await api.post("/admin/resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Résumé PDF updated — the download button now serves your new file.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = async (filename) => {
    try {
      await api.delete(`/admin/photos/${filename}`);
      setContent((c) => ({ ...c, resumePhotos: (c.resumePhotos || []).filter((p) => p !== filename) }));
      toast.success("Photo removed.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setPhotoUploading(true);
    try {
      const { data } = await api.post("/admin/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setContent((c) => ({ ...c, resumePhotos: [...(c.resumePhotos || []), data.file] }));
      toast.success("Photo added to your résumé gallery — press Save changes to keep it.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPhotoUploading(false);
      e.target.value = "";
    }
  };

  const uploadHeadshot = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setPhotoUploading(true);
    try {
      const { data } = await api.post("/admin/headshot", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setContent((c) => ({ ...c, headshot: data.file }));
      toast.success("Headshot updated — it's live on your site now.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPhotoUploading(false);
      e.target.value = "";
    }
  };

  const removeHeadshot = async () => {
    try {
      await api.delete("/admin/headshot");
      setContent((c) => ({ ...c, headshot: "" }));
      toast.success("Headshot reset to the original.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadGraphic = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setPhotoUploading(true);
    try {
      const { data } = await api.post("/admin/graphics", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setContent((c) => ({ ...c, resources: { ...c.resources, graphics: [...(c.resources.graphics || []), data.file] } }));
      toast.success("Graphic added — press Save changes to keep it.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPhotoUploading(false);
      e.target.value = "";
    }
  };

  const removeGraphic = async (filename) => {
    try {
      await api.delete(`/admin/graphics/${filename}`);
      setContent((c) => ({ ...c, resources: { ...c.resources, graphics: (c.resources.graphics || []).filter((g) => g !== filename) } }));
      toast.success("Graphic removed.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setResources = (key, value) => setContent((c) => ({ ...c, resources: { ...c.resources, [key]: value } }));
  const setResLink = (i, field, value) =>
    setContent((c) => ({ ...c, resources: { ...c.resources, links: c.resources.links.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)) } }));
  const addResLink = () =>
    setContent((c) => ({ ...c, resources: { ...c.resources, links: [...c.resources.links, { title: "", url: "", description: "" }] } }));
  const removeResLink = (i) =>
    setContent((c) => ({ ...c, resources: { ...c.resources, links: c.resources.links.filter((_, idx) => idx !== i) } }));

  const dragProps = (list, i) => ({
    onDragStart: startDrag(list, i),
    onDragOver: (e) => e.preventDefault(),
    onDrop: dropOn(list, i),
  });

  const Grip = ({ list, i }) => (
    <span className="cursor-grab text-champagne hover:text-burgundy transition-colors shrink-0" data-testid={`drag-${list}-${i}`} title="Drag to reorder">
      <GripVertical size={16} />
    </span>
  );

  const UpDown = ({ path, i, total }) => (
    <span className="flex flex-col gap-1 shrink-0">
      <button type="button" onClick={() => moveItem(path, i, -1)} disabled={i === 0} data-testid={`move-up-${path.replace(".", "-")}-${i}`} aria-label="Move up" className="text-charcoal/50 hover:text-burgundy disabled:opacity-25 transition-colors">
        <ChevronUp size={14} />
      </button>
      <button type="button" onClick={() => moveItem(path, i, 1)} disabled={i === total - 1} data-testid={`move-down-${path.replace(".", "-")}-${i}`} aria-label="Move down" className="text-charcoal/50 hover:text-burgundy disabled:opacity-25 transition-colors">
        <ChevronDown size={14} />
      </button>
    </span>
  );

  return (
    <main className="space-y-12" data-testid="content-editor">
      <div className="flex items-center justify-between">
        <p className="text-sm text-charcoal/60 max-w-md">
          Edit your site's words below. Drag the grip handles to reorder items. Changes go live the moment you save.
        </p>
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-content-button"
          className="shrink-0 rounded-full bg-burgundy px-8 py-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-ivory hover:bg-charcoal transition-colors disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <section id="editor-header" className={sectionCls} data-testid="editor-header-section">
        <h2 className={headingCls}>Header (top bar)</h2>
        <div className="mt-6 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Logo — left letter</label>
              <input className={inputCls} data-testid="header-logo-left" value={content.header.logoLeft} onChange={(e) => setHeader("logoLeft", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Logo — right letter</label>
              <input className={inputCls} data-testid="header-logo-right" value={content.header.logoRight} onChange={(e) => setHeader("logoRight", e.target.value)} />
            </div>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-charcoal/50 mb-4">Tab names</p>
            <div className="grid md:grid-cols-3 gap-4">
              {["about", "resume", "training", "services", "testimonials", "contact"].map((id) => (
                <div key={id}>
                  <label className={labelCls}>{id}</label>
                  <input className={inputCls} data-testid={`header-tab-${id}`} value={content.header.tabs[id]} onChange={(e) => setHeaderTab(id, e.target.value)} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="editor-colors" className={sectionCls} data-testid="editor-colors-section">
        <h2 className={headingCls}>Accent Colours</h2>
        <p className="mt-2 text-xs text-charcoal/50">Enter any hex colour code (e.g. #D8C5AF) or use the picker.</p>
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          {[["ribbonStar", "Ribbon stars"], ["bullet", "Bullet points"]].map(([key, label]) => (
            <div key={key}>
              <label className={labelCls}>{label}</label>
              <div className="flex items-center gap-3">
                <input type="color" value={content.colors[key]} onChange={(e) => setColor(key, e.target.value)} data-testid={`color-picker-${key}`} className="h-10 w-12 cursor-pointer border border-champagne bg-transparent" />
                <input className={inputCls} data-testid={`color-hex-${key}`} value={content.colors[key]} onChange={(e) => setColor(key, e.target.value)} placeholder="#D8C5AF" />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="editor-hero" className={sectionCls} data-testid="editor-hero-section">
        <h2 className={headingCls}>Hero (top of page)</h2>
        <div className="mt-6 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Name — line one</label>
              <input className={inputCls} data-testid="hero-line1-input" value={content.hero.line1} onChange={(e) => setHero("line1", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Name — line two (italic)</label>
              <input className={inputCls} data-testid="hero-line2-input" value={content.hero.line2} onChange={(e) => setHero("line2", e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Tagline under your name</label>
            <textarea rows={3} className={inputCls} data-testid="hero-tagline-input" value={content.hero.tagline} onChange={(e) => setHero("tagline", e.target.value)} />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Button text</label>
              <input className={inputCls} data-testid="hero-cta-input" value={content.hero.cta} onChange={(e) => setHero("cta", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Photo caption</label>
              <input className={inputCls} data-testid="hero-caption-input" value={content.hero.caption} onChange={(e) => setHero("caption", e.target.value)} />
            </div>
          </div>
          <div className="border border-dashed border-champagne p-5">
            <label className={labelCls}>Headshot photo</label>
            <div className="flex flex-wrap items-center gap-5">
              <img
                src={content.headshot ? `${API}/files/headshots/${content.headshot}` : "/chloe-donan-headshot.png"}
                alt="Current headshot"
                data-testid="headshot-preview"
                className="h-28 w-24 object-cover object-top border border-champagne"
              />
              <div className="space-y-2">
                <label
                  data-testid="headshot-upload-label"
                  className="inline-flex items-center gap-2 cursor-pointer rounded-full border border-burgundy px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.25em] text-burgundy hover:bg-burgundy hover:text-ivory transition-colors"
                >
                  <Upload size={14} />
                  {photoUploading ? "Uploading…" : "Upload new headshot"}
                  <input type="file" accept="image/*" className="hidden" data-testid="headshot-upload-input" onChange={uploadHeadshot} disabled={photoUploading} />
                </label>
                {content.headshot ? (
                  <button onClick={removeHeadshot} data-testid="headshot-remove-button" className="block text-[11px] font-semibold uppercase tracking-[0.25em] text-rose hover:text-burgundy transition-colors">
                    Reset to original
                  </button>
                ) : null}
                <p className="text-xs text-charcoal/50">Shown at the top of your homepage. JPG, PNG or WEBP, up to 15 MB.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="editor-marquee" className={sectionCls} data-testid="editor-marquee-section">
        <h2 className={headingCls}>Scrolling Ribbon</h2>
        <div className="mt-6">
          <label className={labelCls}>Words (comma separated)</label>
          <input
            className={inputCls}
            data-testid="marquee-words-input"
            value={raw.marquee}
            onChange={(e) => setRaw((r) => ({ ...r, marquee: e.target.value }))}
          />
        </div>
      </section>

      <section id="editor-headings" className={sectionCls} data-testid="editor-headings-section">
        <h2 className={headingCls}>Section Headings</h2>
        <p className="mt-2 text-xs text-charcoal/50">The small uppercase label and the large title at the top of each section.</p>
        <div className="mt-6 space-y-5">
          {CHAPTER_LABELS.map(([id, label]) => (
            <div key={id} className="grid md:grid-cols-2 gap-4 border-b border-champagne/60 pb-5" data-testid={`heading-row-${id}`}>
              <div>
                <label className={labelCls}>{label} — small label</label>
                <input className={inputCls} data-testid={`heading-kicker-${id}`} value={content.chapters[id].kicker} onChange={(e) => setChapter(id, "kicker", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>{label} — large title</label>
                <input className={inputCls} data-testid={`heading-title-${id}`} value={content.chapters[id].title} onChange={(e) => setChapter(id, "title", e.target.value)} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="editor-bio" className={sectionCls} data-testid="editor-bio-section">
        <h2 className={headingCls}>Bio</h2>
        <div className="mt-6 space-y-5">
          <div>
            <label className={labelCls}>Opening line (large serif)</label>
            <textarea rows={2} className={inputCls} data-testid="bio-lead-input" value={content.bio.lead} onChange={(e) => setBio("lead", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Bio text</label>
            <textarea rows={10} className={inputCls} data-testid="bio-paragraphs-input" value={content.bio.paragraphs} onChange={(e) => setBio("paragraphs", e.target.value)} />
            <p className="mt-2 text-xs text-charcoal/50">Write freely — leave a blank line between paragraphs and you can have as many as you like.</p>
          </div>
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className={labelCls}>Tags</label>
              <button onClick={() => setBio("tags", [...content.bio.tags, { text: "", url: "", underline: false }])} data-testid="add-tag-button" className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-burgundy hover:text-charcoal transition-colors">
                <Plus size={14} /> Add tag
              </button>
            </div>
            <div className="space-y-4">
              {content.bio.tags.map((t, i) => (
                <div key={i} className="border border-champagne/70 p-4 grid md:grid-cols-2 gap-4 relative" data-testid={`tag-row-${i}`}>
                  <div>
                    <label className={labelCls}>Tag text</label>
                    <input className={inputCls} data-testid={`tag-text-${i}`} value={t.text} onChange={(e) => setBio("tags", content.bio.tags.map((x, idx) => (idx === i ? { ...x, text: e.target.value } : x)))} />
                  </div>
                  <div>
                    <label className={labelCls}>Link URL (optional)</label>
                    <input className={inputCls} data-testid={`tag-url-${i}`} placeholder="https://…" value={t.url} onChange={(e) => setBio("tags", content.bio.tags.map((x, idx) => (idx === i ? { ...x, url: e.target.value } : x)))} />
                  </div>
                  <label className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-charcoal/60 cursor-pointer">
                    <input type="checkbox" checked={!!t.underline} data-testid={`tag-underline-${i}`} onChange={(e) => setBio("tags", content.bio.tags.map((x, idx) => (idx === i ? { ...x, underline: e.target.checked } : x)))} className="accent-[#5A2633] h-4 w-4" />
                    Underlined
                  </label>
                  <button onClick={() => setBio("tags", content.bio.tags.filter((_, idx) => idx !== i))} data-testid={`tag-remove-${i}`} aria-label="Remove tag" className="absolute -top-2 -right-2 bg-ivory border border-champagne rounded-full p-1 text-charcoal/50 hover:text-burgundy transition-colors">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="editor-services" className={sectionCls} data-testid="editor-services-section">
        <div className="flex items-center justify-between">
          <h2 className={headingCls}>Services</h2>
          <button onClick={() => addItem("services")} data-testid="add-service-button" className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-burgundy hover:text-charcoal transition-colors">
            <Plus size={14} /> Add service
          </button>
        </div>
        <div className="mt-6 space-y-6">
          {content.services.map((s, i) => (
            <div key={i} {...dragProps("services", i)} className="border border-champagne/70 p-4 flex gap-3 relative" data-testid={`service-row-${i}`}>
              <span className="flex flex-col items-center gap-2 mt-1 shrink-0">
                <Grip list="services" i={i} />
                <UpDown path="services" i={i} total={content.services.length} />
              </span>
              <div className="flex-1 grid md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Title</label>
                  <input className={inputCls} data-testid={`service-title-${i}`} value={s.title} onChange={(e) => setItem("services", i, "title", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Context label</label>
                  <input className={inputCls} data-testid={`service-context-${i}`} placeholder="Film · TV · Verticals" value={s.context} onChange={(e) => setItem("services", i, "context", e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Description</label>
                  <textarea rows={2} className={inputCls} data-testid={`service-detail-${i}`} value={s.detail} onChange={(e) => setItem("services", i, "detail", e.target.value)} />
                </div>
              </div>
              <button onClick={() => removeItem("services", i)} data-testid={`service-remove-${i}`} aria-label="Remove service" className="absolute -top-2 -right-2 bg-ivory border border-champagne rounded-full p-1 text-charcoal/50 hover:text-burgundy transition-colors">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      </section>

      <section id="editor-resume-card" className={sectionCls} data-testid="editor-resume-card-section">
        <h2 className={headingCls}>Résumé Card & PDF</h2>
        <div className="mt-6 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Card title</label>
              <input className={inputCls} data-testid="resumecard-title-input" value={content.resumeCard.title} onChange={(e) => setResumeCard("title", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Button text</label>
              <input className={inputCls} data-testid="resumecard-button-input" value={content.resumeCard.buttonLabel} onChange={(e) => setResumeCard("buttonLabel", e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Card text</label>
            <textarea rows={2} className={inputCls} data-testid="resumecard-text-input" value={content.resumeCard.text} onChange={(e) => setResumeCard("text", e.target.value)} />
          </div>
          <div className="border border-dashed border-champagne p-5">
            <label className={labelCls}>Résumé PDF</label>
            <div className="flex flex-wrap items-center gap-4">
              <label
                data-testid="resume-upload-label"
                className="inline-flex items-center gap-2 cursor-pointer rounded-full border border-burgundy px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.25em] text-burgundy hover:bg-burgundy hover:text-ivory transition-colors"
              >
                <Upload size={14} />
                {uploading ? "Uploading…" : "Upload new PDF"}
                <input type="file" accept="application/pdf" className="hidden" data-testid="resume-upload-input" onChange={uploadPdf} disabled={uploading} />
              </label>
              <span className="text-xs text-charcoal/50">Replaces the file your visitors download. PDF only, up to 15 MB.</span>
            </div>
          </div>
          <div className="border border-dashed border-champagne p-5">
            <label className={labelCls}>Gallery photos</label>
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <label
                data-testid="photo-upload-label"
                className="inline-flex items-center gap-2 cursor-pointer rounded-full border border-burgundy px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.25em] text-burgundy hover:bg-burgundy hover:text-ivory transition-colors"
              >
                <Upload size={14} />
                {photoUploading ? "Uploading…" : "Upload photo"}
                <input type="file" accept="image/*" className="hidden" data-testid="photo-upload-input" onChange={uploadPhoto} disabled={photoUploading} />
              </label>
              <span className="text-xs text-charcoal/50">Shown beside your credits, automatically cropped to a portrait frame. JPG, PNG or WEBP, up to 15 MB.</span>
            </div>
            {(content.resumePhotos || []).length > 0 ? (
              <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
                {content.resumePhotos.map((f, i) => (
                  <div key={f} {...dragProps("resumePhotos", i)} className="relative cursor-grab" data-testid={`photo-item-${i}`}>
                    <img src={`${API}/files/photos/${f}`} alt={`Gallery photo ${i + 1}`} className="w-full aspect-[3/4] object-cover border border-champagne" />
                    <span className="absolute top-1 left-1 bg-ivory/90 border border-champagne rounded p-0.5 text-charcoal/60" data-testid={`drag-resumePhotos-${i}`} title="Drag to reorder">
                      <GripVertical size={12} />
                    </span>
                    <span className="absolute bottom-1 left-1 flex gap-1">
                      <button type="button" onClick={() => moveItem("resumePhotos", i, -1)} disabled={i === 0} data-testid={`move-earlier-photo-${i}`} aria-label="Move earlier" className="bg-ivory/90 border border-champagne rounded p-0.5 text-charcoal/60 hover:text-burgundy disabled:opacity-25 transition-colors">
                        <ChevronLeft size={12} />
                      </button>
                      <button type="button" onClick={() => moveItem("resumePhotos", i, 1)} disabled={i === content.resumePhotos.length - 1} data-testid={`move-later-photo-${i}`} aria-label="Move later" className="bg-ivory/90 border border-champagne rounded p-0.5 text-charcoal/60 hover:text-burgundy disabled:opacity-25 transition-colors">
                        <ChevronRight size={12} />
                      </button>
                    </span>
                    <button onClick={() => removePhoto(f)} data-testid={`photo-remove-${i}`} aria-label="Remove photo" className="absolute -top-2 -right-2 bg-ivory border border-champagne rounded-full p-1 text-charcoal/50 hover:text-burgundy transition-colors">
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-charcoal/40" data-testid="photos-empty">No photos yet — upload your first one above.</p>
            )}
          </div>
        </div>
      </section>

      <section id="editor-credits" className={sectionCls} data-testid="editor-credits-section">
        <div className="flex items-center justify-between">
          <h2 className={headingCls}>Credits</h2>
          <button onClick={() => addItem("credits")} data-testid="add-credit-button" className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-burgundy hover:text-charcoal transition-colors">
            <Plus size={14} /> Add credit
          </button>
        </div>
        <div className="mt-6 space-y-6">
          {content.credits.map((c, i) => (
            <div key={i} {...dragProps("credits", i)} className="border border-champagne/70 p-4 flex gap-3 relative" data-testid={`credit-row-${i}`}>
              <span className="flex flex-col items-center gap-2 mt-1 shrink-0">
                <Grip list="credits" i={i} />
                <UpDown path="credits" i={i} total={content.credits.length} />
              </span>
              <div className="flex-1 grid md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Type</label>
                  <input className={inputCls} data-testid={`credit-type-${i}`} placeholder="Feature Film" value={c.type} onChange={(e) => setItem("credits", i, "type", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Production</label>
                  <input className={inputCls} data-testid={`credit-production-${i}`} placeholder="Venari" value={c.production} onChange={(e) => setItem("credits", i, "production", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Your role</label>
                  <input className={inputCls} data-testid={`credit-role-${i}`} placeholder="Intimacy Coordinator" value={c.role || ""} onChange={(e) => setItem("credits", i, "role", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Byline</label>
                  <input className={inputCls} data-testid={`credit-byline-${i}`} placeholder="Dir. Taylor Sukert" value={c.byline} onChange={(e) => setItem("credits", i, "byline", e.target.value)} />
                </div>
              </div>
              <button onClick={() => removeItem("credits", i)} data-testid={`credit-remove-${i}`} aria-label="Remove credit" className="absolute -top-2 -right-2 bg-ivory border border-champagne rounded-full p-1 text-charcoal/50 hover:text-burgundy transition-colors">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      </section>

      <section id="editor-training" className={sectionCls} data-testid="editor-training-section">
        <div className="flex items-center justify-between">
          <h2 className={headingCls}>Training & Certification</h2>
          <button onClick={() => addItem("training")} data-testid="add-training-button" className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-burgundy hover:text-charcoal transition-colors">
            <Plus size={14} /> Add training
          </button>
        </div>
        <div className="mt-6 space-y-6">
          {content.training.map((t, i) => (
            <div key={i} {...dragProps("training", i)} className="border border-champagne/70 p-4 flex gap-3 relative" data-testid={`training-row-${i}`}>
              <span className="flex flex-col items-center gap-2 mt-1 shrink-0">
                <Grip list="training" i={i} />
                <UpDown path="training" i={i} total={content.training.length} />
              </span>
              <div className="flex-1 grid md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Title</label>
                  <input className={inputCls} data-testid={`training-title-${i}`} value={t.title} onChange={(e) => setItem("training", i, "title", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Organisation</label>
                  <input className={inputCls} data-testid={`training-org-${i}`} value={t.org} onChange={(e) => setItem("training", i, "org", e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Short description</label>
                  <input className={inputCls} data-testid={`training-detail-${i}`} value={t.detail} onChange={(e) => setItem("training", i, "detail", e.target.value)} />
                </div>
              </div>
              <button onClick={() => removeItem("training", i)} data-testid={`training-remove-${i}`} aria-label="Remove training" className="absolute -top-2 -right-2 bg-ivory border border-champagne rounded-full p-1 text-charcoal/50 hover:text-burgundy transition-colors">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      </section>

      <section id="editor-testimonials" className={sectionCls} data-testid="editor-testimonials-section">
        <div className="flex items-center justify-between">
          <h2 className={headingCls}>Testimonials</h2>
          <button onClick={() => addItem("testimonials")} data-testid="add-testimonial-button" className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-burgundy hover:text-charcoal transition-colors">
            <Plus size={14} /> Add testimonial
          </button>
        </div>
        <p className="mt-2 text-xs text-charcoal/50">Tick "featured" on the one quote shown large at the top.</p>
        <div className="mt-6 space-y-6">
          {content.testimonials.map((t, i) => (
            <div key={i} {...dragProps("testimonials", i)} className="border border-champagne/70 p-4 flex gap-3 relative" data-testid={`testimonial-row-${i}`}>
              <span className="flex flex-col items-center gap-2 mt-1 shrink-0">
                <Grip list="testimonials" i={i} />
                <UpDown path="testimonials" i={i} total={content.testimonials.length} />
              </span>
              <div className="flex-1 space-y-4">
                <div>
                  <label className={labelCls}>Quote</label>
                  <textarea rows={3} className={inputCls} data-testid={`testimonial-quote-${i}`} value={t.quote} onChange={(e) => setItem("testimonials", i, "quote", e.target.value)} />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Name</label>
                    <input className={inputCls} data-testid={`testimonial-name-${i}`} value={t.name} onChange={(e) => setItem("testimonials", i, "name", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>Role / Production</label>
                    <input className={inputCls} data-testid={`testimonial-role-${i}`} value={t.role} onChange={(e) => setItem("testimonials", i, "role", e.target.value)} />
                  </div>
                </div>
                <label className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-charcoal/60 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!t.featured}
                    data-testid={`testimonial-featured-${i}`}
                    onChange={(e) => setItem("testimonials", i, "featured", e.target.checked)}
                    className="accent-[#5A2633] h-4 w-4"
                  />
                  Featured quote
                </label>
              </div>
              <button onClick={() => removeItem("testimonials", i)} data-testid={`testimonial-remove-${i}`} aria-label="Remove testimonial" className="absolute -top-2 -right-2 bg-ivory border border-champagne rounded-full p-1 text-charcoal/50 hover:text-burgundy transition-colors">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      </section>

      <section id="editor-resources" className={sectionCls} data-testid="editor-resources-section">
        <h2 className={headingCls}>Resources Page</h2>
        <p className="mt-2 text-xs text-charcoal/50">Shown at /resources — linked from the top-right tab on your site.</p>
        <div className="mt-6 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Page title</label>
              <input className={inputCls} data-testid="resources-title-input" value={content.resources.title} onChange={(e) => setResources("title", e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Intro text</label>
            <textarea rows={2} className={inputCls} data-testid="resources-intro-input" value={content.resources.intro} onChange={(e) => setResources("intro", e.target.value)} />
          </div>

          <div className="border-t border-champagne/60 pt-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-charcoal/50">Links (websites, videos, PDFs)</p>
              <button onClick={addResLink} data-testid="add-resource-link-button" className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-burgundy hover:text-charcoal transition-colors">
                <Plus size={14} /> Add link
              </button>
            </div>
            <div className="space-y-6">
              {(content.resources.links || []).map((l, i) => (
                <div key={i} {...dragProps("resources.links", i)} className="border border-champagne/70 p-4 flex gap-3 relative" data-testid={`resource-link-row-${i}`}>
                  <span className="flex flex-col items-center gap-2 mt-1 shrink-0">
                    <Grip list="resources.links" i={i} />
                    <UpDown path="resources.links" i={i} total={content.resources.links.length} />
                  </span>
                  <div className="flex-1 space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className={labelCls}>Title</label>
                        <input className={inputCls} data-testid={`resource-title-${i}`} placeholder="Intimacy on set guidelines" value={l.title} onChange={(e) => setResLink(i, "title", e.target.value)} />
                      </div>
                      <div>
                        <label className={labelCls}>URL</label>
                        <input className={inputCls} data-testid={`resource-url-${i}`} placeholder="https://…" value={l.url} onChange={(e) => setResLink(i, "url", e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Short description (optional)</label>
                      <input className={inputCls} data-testid={`resource-description-${i}`} value={l.description} onChange={(e) => setResLink(i, "description", e.target.value)} />
                    </div>
                  </div>
                  <button onClick={() => removeResLink(i)} data-testid={`resource-link-remove-${i}`} aria-label="Remove link" className="absolute -top-2 -right-2 bg-ivory border border-champagne rounded-full p-1 text-charcoal/50 hover:text-burgundy transition-colors">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              {(content.resources.links || []).length === 0 && (
                <p className="text-xs text-charcoal/40" data-testid="resources-links-empty">No links yet — add your first one above.</p>
              )}
            </div>
          </div>

          <div className="border border-dashed border-champagne p-5">
            <label className={labelCls}>Infographics gallery</label>
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <label
                data-testid="graphic-upload-label"
                className="inline-flex items-center gap-2 cursor-pointer rounded-full border border-burgundy px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.25em] text-burgundy hover:bg-burgundy hover:text-ivory transition-colors"
              >
                <Upload size={14} />
                {photoUploading ? "Uploading…" : "Upload graphic"}
                <input type="file" accept="image/*" className="hidden" data-testid="graphic-upload-input" onChange={uploadGraphic} disabled={photoUploading} />
              </label>
              <span className="text-xs text-charcoal/50">Shown under the links on your Resources page. JPG, PNG or WEBP.</span>
            </div>
            {(content.resources.graphics || []).length > 0 ? (
              <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
                {content.resources.graphics.map((f, i) => (
                  <div key={f} {...dragProps("resources.graphics", i)} className="relative cursor-grab" data-testid={`graphic-item-${i}`}>
                    <img src={`${API}/files/graphics/${f}`} alt={`Infographic ${i + 1}`} className="w-full aspect-square object-cover border border-champagne" />
                    <span className="absolute top-1 left-1 bg-ivory/90 border border-champagne rounded p-0.5 text-charcoal/60" data-testid={`drag-resources-graphics-${i}`} title="Drag to reorder">
                      <GripVertical size={12} />
                    </span>
                    <span className="absolute bottom-1 left-1 flex gap-1">
                      <button type="button" onClick={() => moveItem("resources.graphics", i, -1)} disabled={i === 0} data-testid={`move-earlier-graphic-${i}`} aria-label="Move earlier" className="bg-ivory/90 border border-champagne rounded p-0.5 text-charcoal/60 hover:text-burgundy disabled:opacity-25 transition-colors">
                        <ChevronLeft size={12} />
                      </button>
                      <button type="button" onClick={() => moveItem("resources.graphics", i, 1)} disabled={i === content.resources.graphics.length - 1} data-testid={`move-later-graphic-${i}`} aria-label="Move later" className="bg-ivory/90 border border-champagne rounded p-0.5 text-charcoal/60 hover:text-burgundy disabled:opacity-25 transition-colors">
                        <ChevronRight size={12} />
                      </button>
                    </span>
                    <button onClick={() => removeGraphic(f)} data-testid={`graphic-remove-${i}`} aria-label="Remove graphic" className="absolute -top-2 -right-2 bg-ivory border border-champagne rounded-full p-1 text-charcoal/50 hover:text-burgundy transition-colors">
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-charcoal/40" data-testid="graphics-empty">No graphics yet — upload your first one above.</p>
            )}
          </div>
        </div>
      </section>

      <section id="editor-contact" className={sectionCls} data-testid="editor-contact-section">
        <h2 className={headingCls}>Contact Section</h2>
        <div className="mt-6 space-y-5">
          <div>
            <label className={labelCls}>Large serif line</label>
            <textarea rows={2} className={inputCls} data-testid="contact-lead-input" value={content.contact.lead} onChange={(e) => setContactField("lead", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Info lines (one per line)</label>
            <textarea
              rows={3}
              className={inputCls}
              data-testid="contact-lines-input"
              value={raw.contactLines}
              onChange={(e) => setRaw((r) => ({ ...r, contactLines: e.target.value }))}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4 border-t border-champagne/60 pt-5">
            <div>
              <label className={labelCls}>Project type dropdown — label</label>
              <input className={inputCls} data-testid="contact-label-projectType" value={content.contact.labels.projectType} onChange={(e) => setContactNested("labels", "projectType", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Dropdown placeholder</label>
              <input className={inputCls} data-testid="contact-placeholder-projectType" value={content.contact.placeholders.projectType} onChange={(e) => setContactNested("placeholders", "projectType", e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Dropdown options (one per line)</label>
            <textarea rows={5} className={inputCls} data-testid="contact-projecttypes-input" value={raw.projectTypes} onChange={(e) => setRaw((r) => ({ ...r, projectTypes: e.target.value }))} />
          </div>
          <div>
            <label className={labelCls}>Script upload field label</label>
            <input className={inputCls} data-testid="contact-label-script" value={content.contact.labels.script} onChange={(e) => setContactNested("labels", "script", e.target.value)} />
          </div>
          <div className="border-t border-champagne/60 pt-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-charcoal/50 mb-4">Text sizes (pixels)</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[["lead", "Large serif line"], ["lines", "Info lines"], ["label", "Form labels"], ["input", "Text inside boxes"]].map(([key, label]) => (
                <div key={key}>
                  <label className={labelCls}>{label}</label>
                  <input
                    type="number"
                    min={10}
                    max={48}
                    className={inputCls}
                    data-testid={`fontsize-${key}`}
                    value={content.contact.fontSizes?.[key] ?? ""}
                    onChange={(e) => setContactNested("fontSizes", key, Number(e.target.value) || 12)}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="border-t border-champagne/60 pt-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-charcoal/50 mb-4">Form wording</p>
            <div className="space-y-4">
              {FORM_FIELDS.map(([key, label]) => (
                <div key={key} className="grid md:grid-cols-2 gap-4" data-testid={`contact-field-${key}`}>
                  <div>
                    <label className={labelCls}>{label} — label</label>
                    <input className={inputCls} data-testid={`contact-label-${key}`} value={content.contact.labels[key]} onChange={(e) => setContactNested("labels", key, e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>{label} — placeholder inside the box</label>
                    <input className={inputCls} data-testid={`contact-placeholder-${key}`} value={content.contact.placeholders[key]} onChange={(e) => setContactNested("placeholders", key, e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="border-t border-champagne/60 pt-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-charcoal/50 mb-2">Required fields</p>
            <p className="text-xs text-charcoal/50 mb-4">People will see a red asterisk beside required labels and can't submit until they're filled in.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {REQUIRABLE_FIELDS.map(([key, label]) => (
                <label key={key} className="inline-flex items-center gap-2 text-sm text-charcoal cursor-pointer" data-testid={`required-toggle-${key}`}>
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[#5A2633]"
                    checked={!!content.contact.requiredFields?.[key]}
                    onChange={(e) =>
                      setContactNested(
                        "requiredFields",
                        key,
                        e.target.checked,
                      )
                    }
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="border-t border-champagne/60 pt-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-charcoal/50 mb-4">Union / Non-Union tickboxes</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Field label (shown above the tickboxes)</label>
                <input
                  className={inputCls}
                  data-testid="contact-label-union"
                  value={content.contact.labels?.union || ""}
                  onChange={(e) => setContactNested("labels", "union", e.target.value)}
                />
              </div>
              <div>
                <label className={labelCls}>Validation message shown when required fields are empty</label>
                <input
                  className={inputCls}
                  data-testid="contact-validation-message-input"
                  value={content.contact.validationMessage || ""}
                  onChange={(e) => setContactField("validationMessage", e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4 border-t border-champagne/60 pt-5">
            <div>
              <label className={labelCls}>Send button text</label>
              <input className={inputCls} data-testid="contact-submit-label-input" value={content.contact.submitLabel} onChange={(e) => setContactField("submitLabel", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>While sending</label>
              <input className={inputCls} data-testid="contact-sending-label-input" value={content.contact.sendingLabel} onChange={(e) => setContactField("sendingLabel", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Success message</label>
              <input className={inputCls} data-testid="contact-success-input" value={content.contact.successMessage} onChange={(e) => setContactField("successMessage", e.target.value)} />
            </div>
          </div>
        </div>
      </section>

      <section id="editor-footer" className={sectionCls} data-testid="editor-footer-section">
        <h2 className={headingCls}>Footer</h2>
        <div className="mt-6 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Name</label>
              <input className={inputCls} data-testid="footer-name-input" value={content.footer.name} onChange={(e) => setFooter("name", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Tagline</label>
              <input className={inputCls} data-testid="footer-tagline-input" value={content.footer.tagline} onChange={(e) => setFooter("tagline", e.target.value)} />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Instagram URL</label>
              <input className={inputCls} data-testid="footer-instagram-url-input" value={content.footer.instagramUrl} onChange={(e) => setFooter("instagramUrl", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Instagram label</label>
              <input className={inputCls} data-testid="footer-instagram-label-input" value={content.footer.instagramLabel} onChange={(e) => setFooter("instagramLabel", e.target.value)} />
            </div>
          </div>
        </div>
      </section>

      <div className="flex justify-end pb-12">
        <button
          onClick={save}
          disabled={saving}
          data-testid="save-content-button-bottom"
          className="rounded-full bg-burgundy px-8 py-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-ivory hover:bg-charcoal transition-colors disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </main>
  );
};
